import 'reflect-metadata';
import { DEFAULT_METADATA_STORAGE, TransformationType, TransformFnParams } from "@nts/std/serialization";
import { getMetadataStorage, MetadataStorage } from 'class-validator';
import { DateTimeOffset } from './date-time-offset';
import { BigNumber } from './big-number';

export class DomainModelExtension {

  parent: any;

  static readonly EXTENSION_DATA_PROPERTY_NAME = 'extension';

  /**
   * Returns an instance of a dynamically created class with primitive properties and @Expose decorators for class-transformer serialization.
   */
  static toExposedClassInstance(value: DomainModelExtension): any {
      if (
          value == null ||
          !(typeof value === 'object') ||
          !(Object.keys(value)?.length > 0)
      ) {
          return null;
      }
      // Dynamically create a class
      class ExposedDynamic {}
      const simpleProps: Record<string, any> = {};
      const metadataStorage = getMetadataStorage() as MetadataStorage;
      for (const key of Object.keys(value)) {
          let propValue = value[key];
          if (
              propValue === null ||
              propValue === undefined ||
              typeof propValue === 'string' ||
              typeof propValue === 'number' ||
              typeof propValue === 'boolean' ||
              propValue instanceof Date ||
              propValue instanceof DateTimeOffset
          ) {

              // Visto che non aggiungiamo il decoratore @Transform
              // per gestire DateTimeOffset, lo convertiamo in stringa
              if(propValue instanceof DateTimeOffset) {
                  propValue = propValue.toString();
              }



                Object.defineProperty(ExposedDynamic.prototype, key, {
                  value: propValue,
                  writable: true,
                  enumerable: true,
                  configurable: true
              });

              // Apply @Expose decorator
              DEFAULT_METADATA_STORAGE.addExposeMetadata({
                    target: ExposedDynamic.prototype.constructor,
                  propertyName: key,
                  options: {},
              });

              simpleProps[key] = propValue;
          }
      }
        return new ExposedDynamic();
  }
  /**
   * Builds a ExtensionData proxy from a plain object.
   */
  static fromPlainObject(data: Partial<Record<string, any>>): DomainModelExtension {
      const instance = new DomainModelExtension(data);
      return instance.getProxy();
  }
  /**
   * Returns a plain object containing only primitive properties (string, number, boolean, null, undefined).
   */
  static toPlainSimpleObject(value: DomainModelExtension): Record<string, any> | null {
      if (
          value == null ||
          !(typeof value === 'object') ||
          !(Object.keys(value)?.length > 0)
      ) {
          return null;
      }
      const simpleProps: Record<string, any> = {};
      for (const key of Object.keys(value)) {
          const propValue = value[key];
          if (
              propValue === null ||
              propValue === undefined ||
              typeof propValue === 'string' ||
              typeof propValue === 'number' ||
              typeof propValue === 'boolean'
          ) {
              simpleProps[key] = propValue;
          }
      }
      return simpleProps;
  }

  customGet: (get: () => any, propertyName: string) => any = null;
  customSet: (set: () => void, value: any, propertyName: string, oldValue: any) => void = null;

  private props: Partial<Record<string, any>> = {};

  constructor(initial?: Partial<Record<string, any>>, callcback?: (data: Partial<Record<string, any>>) => void) {
      if (initial) {
          this.props = { ...initial };
      }
  }

  get(propertyName: string): any | undefined {
      if (this.customGet) {
          return this.customGet(() => this.props[propertyName], propertyName) as any;
      }
      return this.props[propertyName];
  }

  set(propertyName: string, value: any): void {

      if (this.customSet) {
          this.customSet(() => { this.props[propertyName] = value; }, value, propertyName, this.props[propertyName]);
          return;
      }

      const isSameValue = this.compareValues(this.get(propertyName), value)
      if (!isSameValue) {
          this.props[propertyName] = value;

          if (this.parent.emitChanges) {
              this.parent.propertyChanging.emit(propertyName);
          }

          this.parent.applyChangeState();

            if (this.parent.emitChanges) {
              this.parent.propertyChanged.emit(propertyName);
          }
      }

  }

  compareValues(v1: any, v2: any, operator: string = '==') {
      if (BigNumber.isBigNumber(v1) || BigNumber.isBigNumber(v2)) {
          return BigNumber.compareValues(v1, v2, operator);
      }

      switch (operator) {
          case '==':
              return v1 == v2;
          default:
              throw new Error(`not implemented sign ${operator} in compareValues`);
      }
  }

  has(propertyName: string): boolean {
      return propertyName in this.props;
  }

  keys(): (string | symbol)[] {
      return Object.keys(this.props) as (string | symbol)[];
  }

  toPlainObject(): Partial<Record<string, any>> {
      return { ...this.props };
  }

  getProxy(): any & { toJSON(): any } {
      const self = this;

      return new Proxy(this as any, {
      get(target, prop, receiver) {
          if (prop === 'customGet') {
              return self.customGet ? self.customGet.bind(receiver) : undefined;
          }
          if (prop === 'customSet') {
              return self.customSet ? self.customSet.bind(receiver) : undefined;
          }
          if (prop === 'toJSON') {
          return () => self.toPlainObject();
          }
          if (typeof prop === "string" && prop in self.props) {
          return self.get(prop);
          }
          return undefined;
      },
      set(_, prop, value) {
          if (typeof value === 'function') {
              if (prop === 'customGet') {
                  self.customGet = value as (get: () => any, propertyName: string) => any;
                  return true;
              }
              if (prop === 'customSet') {
                  self.customSet = value as (set: () => void, value: any, propertyName: string, oldValue: any) => void;
                  return true;
              }
          }

          if (typeof prop === "string" && prop === 'parent') {
              self.parent = value;
              return true;
          } else if(typeof prop === "string")  {
              self.set(prop, value);
              return true;
          }

          return false;
      },
      has(_, propertyName) {
          return typeof propertyName === "string" && self.has(propertyName);
      },
      ownKeys() {
          return self.keys();
      },
      getOwnPropertyDescriptor(_, propertyName) {
          if (typeof propertyName === "string" && self.has(propertyName)) {
          return {
              enumerable: true,
              configurable: true
          };
          }
          return undefined;
      }
      });
  }

  // Metodo statico per costruire da JSON
  static fromJSON(data: Partial<Record<string, any>>): DomainModelExtension {
      const instance = new DomainModelExtension(data);
      return instance.getProxy();
  }

  static transform(params: TransformFnParams, propertyName?: string) {

      const property = propertyName ?? params.key;

      if (params.type === TransformationType.CLASS_TO_PLAIN) {
          return {...params.obj[property]}
      } else {
          return DomainModelExtension.fromPlainObject(params.obj[property]);
      }
  }

  static formatToString(value: DomainModelExtension): string | null {
      if (
          value == null ||
          !(typeof value === 'object') ||
          !(Object.keys(value)?.length > 0)
      ) {
          return null;
      }
      // Only serialize primitive properties
      const simpleProps: Record<string, any> = {};
      for (const key of Object.keys(value)) {
          const propValue = value[key];
          if (
              propValue === null ||
              propValue === undefined ||
              typeof propValue === 'string' ||
              typeof propValue === 'number' ||
              typeof propValue === 'boolean'
          ) {
              simpleProps[key] = propValue;
          }
      }
      return JSON.stringify(simpleProps);
  }

  static formatFromString(value: string): DomainModelExtension {
          if (value == null || value === '') {
              return DomainModelExtension.fromJSON({});
          }
          try {
              const obj = JSON.parse(value);
              return DomainModelExtension.fromJSON(obj);
          } catch {
              return DomainModelExtension.fromJSON({});
          }
  }
}
